import Area from "../chatframe/Area";
import globalPubSubInstance from "./pubSubGlobal";

export default class ChatPosition {
  /**
   * If the chat is on the right side of the screen, move the chat left.
   * If it's on the left, move the chat right.
   */
  static togglePosition() {
    ChatPosition.moveChatToOppositePosition();
    ChatPosition.saveWindowPosition();
    ChatPosition.updateMoveButtonDirection();
  }

  private static moveChatToOppositePosition() {
    window.messagingApp.messageWindow.moveStage(
      ChatPosition.getChatDistanceFromRightEdgeOfScreen()
    );
  }

  private static saveWindowPosition() {
    const bottom = ChatPosition.getChatDistanceFromBottomEdgeOfScreen();
    const right = ChatPosition.getChatDistanceFromRightEdgeOfScreen();

    window.messagingApp.getSDKInstance().saveWindowPosition(bottom, right);
  }

  private static getChatDistanceFromBottomEdgeOfScreen(): number {
    const chatAreaBottom = ChatPosition.getChatArea().b;
    const customBottomCalculation = () =>
      window.parent.innerHeight - ChatPosition.getChatBoundingRect().bottom;

    /**
     * When the chat starts we don't always have the Chat Area values in
     * `messagingApp.messageWindow.area` yet, so we have to calculate it
     * ourselves. But it's better to use Chat Area from `messageWindow` when we
     * can because it's referenced by other parts of the code.
     */
    if (typeof chatAreaBottom === "number" && !Number.isNaN(chatAreaBottom)) {
      return chatAreaBottom;
    } else {
      return customBottomCalculation();
    }
  }

  private static getChatDistanceFromLeftEdgeOfScreen(): number {
    return (
      window.parent.innerWidth -
      ChatPosition.getChatDistanceFromRightEdgeOfScreen() -
      ChatPosition.getChatArea().width
    );
  }

  private static getChatDistanceFromRightEdgeOfScreen(): number {
    const chatAreaRight = ChatPosition.getChatArea().r;
    const customRightCalculation = () =>
      window.parent.innerWidth - ChatPosition.getChatBoundingRect().right;

    /**
     * When the chat starts we don't always have the Chat Area values in
     * `messagingApp.messageWindow.area` yet, so we have to calculate it
     * ourselves. But it's better to use Chat Area from `messageWindow` when we
     * can because it's referenced by other parts of the code.
     */
    if (typeof chatAreaRight === "number" && !Number.isNaN(chatAreaRight)) {
      return chatAreaRight;
    } else {
      return customRightCalculation();
    }
  }

  private static getChatArea(): Area {
    return window.messagingApp.messageWindow.area;
  }

  private static getChatBoundingRect(): DOMRect {
    return (window.messagingApp.chatEl as HTMLElement).getBoundingClientRect();
  }

  static updateMoveButtonDirection() {
    const currentPosition = ChatPosition.getCurrentLeftOrRightPosition();

    if (currentPosition === "right") {
      ChatPosition.flipMoveButtonToPoint("left");
    } else {
      ChatPosition.flipMoveButtonToPoint("right");
    }
  }

  /**
   * Returns "left" or "right" depending on if the chat is in the left half or
   * right half of the screen
   */
  private static getCurrentLeftOrRightPosition(): "left" | "right" {
    if (
      ChatPosition.getChatDistanceFromLeftEdgeOfScreen() >
      ChatPosition.getChatDistanceFromRightEdgeOfScreen()
    ) {
      return "right";
    } else {
      return "left";
    }
  }

  /**
   * Flip the icon to point "left" or "right". Assuming the icon is pointing
   * left by default, otherwise using css to flip it to point right.
   */
  private static flipMoveButtonToPoint(direction: "left" | "right") {
    ChatPosition.getMoveButtonIcons().forEach((icon) => {
      if (!(icon instanceof HTMLElement)) return;

      if (direction === "right") {
        icon.style.transform = "scaleX(-1)";
      } else {
        icon.style.transform = "";
      }
    });
  }

  /**
   * Get the icon elements for "Move Chat". They can be in different positions
   * such as topbar, topbar menu, next to input, or extended footer; Depending
   * on how the Move element is configured in uiconfig. They can also be in all
   * those places at the same time as well.
   */
  private static getMoveButtonIcons(): NodeListOf<Element> {
    const moveButtonIconsSelectors = [
      "topbar-element[type=move] div.icon",
      "overflow-item[type=move] img",
      "footer-button[type=move] input",
      "topbar-element[type=move] span.icon",
    ];

    return document.querySelectorAll(moveButtonIconsSelectors.join(", "));
  }
}

// Update icon pointing direction when chat has moved
globalPubSubInstance.subscribe(
  ChatPosition.updateMoveButtonDirection,
  "MessagingWindow.updatePosition"
);
